/**
 * THE MEASURE VOICE (narration slice 1b, lead rulings 2026-09-27 09:40 (A)/(B)). How a partner
 * says one measure family, authored ONCE per family as publication-only data (one versioned
 * file per family ref under ./voice; onboarding data: a new company brings its voice files).
 * The server hands each output's family ref (and dataset) to publication; the reader catalog
 * never loads these files. A family without a file keeps today's form.
 *
 * A template places typed slots only: {scope} (the governed, display-named scope), {v} (the
 * binding placeholder), {period} (the typed period phrase), {direction} (words picked from the
 * SAME binding's sign × the family's polarity). No template carries a digit.
 */
import cost from './voice/cost_to_achieve_total.voice.v1.json';
import glTotal from './voice/functional_gl_total.voice.v1.json';
import headcount from './voice/headcount_total.voice.v1.json';
import leverSavings from './voice/lever_savings.voice.v1.json';
import leverSavingsRate from './voice/lever_savings_rate.voice.v1.json';
import laborCost from './voice/org_labor_cost_total.voice.v1.json';
import opex from './voice/operating_expense_total.voice.v1.json';
import sga from './voice/sga_spend_total.voice.v1.json';
import planned from './voice/synergy_planned_value.voice.v1.json';
import variance from './voice/synergy_plan_variance.voice.v1.json';
import realized from './voice/synergy_realized_value.voice.v1.json';
import target from './voice/synergy_target_run_rate.voice.v1.json';

export type AskVoicePolarity = 'benefit' | 'cost' | 'risk';
export type AskDirectionWords = Readonly<{ above: string; below: string; level: string }>;

export type AskMeasureVoice = Readonly<{
  familyRef: string;
  version: number;
  /** flow: a period ("in 2024"); stock: a date ("as at 14 Sep 2026"). */
  periodStyle: 'flow' | 'stock';
  polarity: AskVoicePolarity;
  /** The side a gap is measured from: `reference` (plan, budget) or `actual`. */
  role?: 'actual' | 'reference';
  /** "{scope} cost {v} in the general ledger {period}." */
  sentence: string;
  /** A gap between this (actual) side and its reference: "{scope} is {v} {direction} {period}." */
  gapSentence?: string;
  directionWords?: AskDirectionWords;
  /** By grouping dimension: "{#1} is {top} at {v}, ahead of {#2} at {v}." */
  rankNouns?: Readonly<Record<string, Readonly<{ top: string; bottom: string }>>>;
  /** A dataset's rank nouns, by grouping dimension, override the family's nouns. */
  datasetRankNouns?: Readonly<
    Record<string, Readonly<Record<string, Readonly<{ top: string; bottom: string }>>>>
  >;
  /** By dimension, then member ref: "Amendment required" → "needs a plan amendment". */
  statusPhrases?: Readonly<Record<string, Readonly<Record<string, string>>>>;
  /** A dataset that uses the family with a different meaning (eliminations: "eliminates"). */
  datasetSentences?: Readonly<Record<string, string>>;
}>;

export const ASK_MEASURE_VOICES: readonly AskMeasureVoice[] = Object.freeze(
  [
    cost,
    glTotal,
    headcount,
    leverSavings,
    leverSavingsRate,
    laborCost,
    opex,
    sga,
    planned,
    variance,
    realized,
    target,
  ].map((voice) => voice as AskMeasureVoice)
);

/** Default direction words by polarity (a family may override them). */
const DIRECTION_WORDS: Readonly<Record<AskVoicePolarity, AskDirectionWords>> = {
  benefit: { above: 'ahead of plan', below: 'behind plan', level: 'on plan' },
  cost: { above: 'over budget', below: 'under budget', level: 'on budget' },
  risk: { above: 'above its limit', below: 'within its limit', level: 'at its limit' },
};

export function measureVoice(
  familyRef: string | null | undefined,
  voices: readonly AskMeasureVoice[] = ASK_MEASURE_VOICES
): AskMeasureVoice | null {
  return familyRef ? (voices.find((voice) => voice.familyRef === familyRef) ?? null) : null;
}

/**
 * The words for a signed engine value (lead guard (B)): the SAME binding's sign × the family's
 * polarity; never a separate source. Zero reads as level ("on plan").
 */
export function directionPhrase(voice: AskMeasureVoice, value: number): string {
  const words = voice.directionWords ?? DIRECTION_WORDS[voice.polarity];
  return value > 0 ? words.above : value < 0 ? words.below : words.level;
}

/** The family's sentence for this dataset (a dataset override wins). */
export function voiceSentence(voice: AskMeasureVoice, datasetRef: string | null): string {
  return (datasetRef && voice.datasetSentences?.[datasetRef]) || voice.sentence;
}

/** Everything wrong with a voice file, named; empty is the only loadable state. */
export function measureVoiceProblems(voice: AskMeasureVoice): readonly string[] {
  const problems: string[] = [];
  const templates = [
    voice.sentence,
    ...(voice.gapSentence !== undefined ? [voice.gapSentence] : []),
    ...Object.values(voice.datasetSentences ?? {}),
  ];
  for (const template of templates) {
    if (!template.includes('{v}')) problems.push(`${voice.familyRef}: template has no {v}`);
    if (!template.includes('{scope}')) problems.push(`${voice.familyRef}: template has no {scope}`);
    if (/\d/.test(template)) problems.push(`${voice.familyRef}: template carries a digit`);
    const slots = template.match(/\{[a-z]+\}/g) ?? [];
    for (const slot of slots) {
      if (!['{scope}', '{v}', '{period}', '{direction}'].includes(slot))
        problems.push(`${voice.familyRef}: unknown slot ${slot}`);
    }
  }
  if (voice.gapSentence && !voice.gapSentence.includes('{direction}'))
    problems.push(`${voice.familyRef}: gapSentence has no {direction}`);
  return problems;
}
